import { BlockAPI, ManifestV2 } from "@softr-io/blocks-sdk";
import { BlockProps } from "@softr-io/blocks-sdk/react";
import { FC } from "react";
import { appendLinkTagToHead } from "./appendLinkTagToHead";
import { retry } from "./retry";
import { BlockAPIReadyEvent } from "./BlockAPIReadyEvent";

interface FetchManifestV2BlockArgs {
    manifest: ManifestV2;
    baseUrl: string;
    blockName: string;
    blockVersion: string;
}
type BlockComponent = FC<BlockProps> & {
    api?: BlockAPI | undefined;
};

const fetchManifestV2ComponentCache = new Map<string, Promise<BlockComponent>>();

export async function fetchManifestV2Block(args: FetchManifestV2BlockArgs): Promise<BlockComponent> {
    window.__softrCSSImport = __softrCSSImport;
    const [Component] = await Promise.all([fetchManifestV2Component(args), ...fetchManifestV2Styles(args.manifest)]);
    return Component;
}
async function fetchManifestV2Component(args: FetchManifestV2BlockArgs) {
    const key = `${args.baseUrl}/${args.blockName}/${args.blockVersion}/${args.manifest.main}`;
    const existing = fetchManifestV2ComponentCache.get(key);
    if (existing) {
        return existing;
    }
    const promise = _fetchESMComponent(args);
    fetchManifestV2ComponentCache.set(key, promise);
    return promise;
}
async function _fetchESMComponent({
    blockName,
    baseUrl,
    blockVersion,
    manifest: { main },
}: FetchManifestV2BlockArgs): Promise<BlockComponent> {
    const path = `${baseUrl}/${blockName}/${blockVersion}/${main.replace(/^\.\//, "")}`;

    const baseUrlWithoutBlocks = baseUrl.replace(/\/blocks(\/)?$/, "");
    window.SOFTR_BASE_URL = baseUrlWithoutBlocks;

    // Webpack expects dynamic imports to statically resolve, but this
    // is an actual dynamic import, so we need to tell webpack
    // to not try to resolve this import at build time using
    // webpackIgnore: true
    const module: { default: BlockComponent } = await retry(
        () =>
            import(
                /* webpackIgnore: true */
                path
            ),
    );

    BlockAPIReadyEvent.dispatch({
        blockName,
        blockVersion,
        api: module.default.api,
    });
    return module.default;
}

function fetchManifestV2Styles(manifest: ManifestV2): Promise<unknown>[] {
    return manifest.css.map((path) => __softrCSSImport(path));
}

/**
 * Each block's main bundle is expected to call this function to load its own CSS.
 * See `build.ts` for more details.
 */
async function __softrCSSImport(cssPath: string) {
    const href = window.SOFTR_BASE_URL + "/" + cssPath;
    return appendLinkTagToHead(href);
}

declare global {
    interface Window {
        SOFTR_BASE_URL: string;
        __softrCSSImport: (cssPath: string) => Promise<unknown>;
    }
}
